import { useState, useCallback, useEffect, useContext } from "react";
import axios from "axios";
import { toast } from "../components/ui/use-toast";
import { AppContext } from "../contexts/AppContext";

// Create a dedicated axios instance with the correct base URL
const savedSchedulesAxios = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "https://isco-backend-deployment-932661235642.us-central1.run.app",
  headers: {
    "Content-Type": "application/json"
  }
});

export default function useSavedSchedules() {
  const { data: contextData, setData: setContextData } = useContext(AppContext) || {};
  const [savedSchedules, setSavedSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedScheduleId, setSelectedScheduleId] = useState(null);

  // Fetch all saved schedules
  const fetchSavedSchedules = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      console.log("Fetching saved schedules...");
      const response = await savedSchedulesAxios.get("/schedules/saved");
      
      if (response.data) {
        console.log(`Received ${response.data.length} saved schedules`);
        setSavedSchedules(response.data);
        
        // If there are schedules and none is selected, select the first one
        if (response.data.length > 0 && !selectedScheduleId) {
          setSelectedScheduleId(response.data[0].cluster);
        }
      }
      return response.data;
    } catch (err) {
      console.error("Error fetching saved schedules:", err);
      const errorMessage = 
        err.response?.data?.error || 
        err.message || 
        "Failed to fetch saved schedules.";
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, [selectedScheduleId]);

  // Save or unsave a schedule
  const toggleSaveSchedule = useCallback(async (scheduleId, isSaved) => {
    try {
      console.log(`${isSaved ? 'Saving' : 'Unsaving'} schedule ${scheduleId}...`);
      const response = await savedSchedulesAxios.post(`/schedules/${scheduleId}/save`, {
        is_saved: isSaved
      });
      
      if (response.data) {
        console.log(`Schedule ${scheduleId} ${isSaved ? 'saved' : 'unsaved'} successfully`);
        
        // Update local state
        if (isSaved) {
          // Add to saved schedules if not already there
          setSavedSchedules(prev => {
            const exists = prev.some(s => String(s.cluster) === String(scheduleId));
            if (!exists && response.data) {
              return [...prev, response.data];
            }
            return prev;
          });
        } else {
          // Remove from saved schedules
          setSavedSchedules(prev => prev.filter(s => String(s.cluster) !== String(scheduleId)));
        }
        
        // Update AppContext data to sync with ClusterListPage
        if (contextData && setContextData) {
          console.log(`Updating contextData for scheduleId: ${scheduleId}, isSaved: ${isSaved}`);
          console.log(`Current contextData has ${contextData.length} items`);
          
          // Use functional update to ensure we're working with the latest state
          setContextData(prevContextData => {
            if (!prevContextData || !Array.isArray(prevContextData)) {
              console.warn('contextData is not an array:', prevContextData);
              return prevContextData;
            }
            
            const updatedContextData = prevContextData.map((item) => {
              const itemId = String(item.id || item.cluster);
              const targetId = String(scheduleId);
              const isMatch = itemId === targetId;
              if (isMatch) {
                console.log(`Found matching item: ${itemId}, updating is_saved from ${item.is_saved} to: ${isSaved}`);
                return { ...item, is_saved: isSaved };
              }
              return item;
            });
            
            // Verify the update was applied
            const updatedItem = updatedContextData.find(item => 
              String(item.id || item.cluster) === String(scheduleId)
            );
            if (updatedItem) {
              console.log(`Verification: item ${scheduleId} now has is_saved: ${updatedItem.is_saved}`);
            } else {
              console.warn(`Could not find item ${scheduleId} in contextData after update`);
            }
            
            return updatedContextData;
          });
        }
        
        toast({
          title: 'Success',
          description: `Schedule ${isSaved ? 'saved' : 'unsaved'} successfully`,
        });
        
        return true;
      }
      return false;
    } catch (err) {
      console.error(`Error ${isSaved ? 'saving' : 'unsaving'} schedule ${scheduleId}:`, err);
      const errorMessage = 
        err.response?.data?.error || 
        err.message || 
        `Failed to ${isSaved ? 'save' : 'unsave'} schedule.`;
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      return false;
    }
  }, []);

  // Load saved schedules on mount
  useEffect(() => {
    // Make sure the auth header is set in axios defaults
    const token = localStorage.getItem('isco_jwt');
    if (token) {
      console.log("Token found, setting Authorization header");
      savedSchedulesAxios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    
    fetchSavedSchedules();
  }, [fetchSavedSchedules]);

  // Get the selected schedule from state
  const selectedSchedule = selectedScheduleId
    ? savedSchedules.find(s => s.cluster === selectedScheduleId)
    : null;

  return {
    savedSchedules,
    loading,
    error,
    selectedScheduleId,
    selectedSchedule,
    setSelectedScheduleId,
    fetchSavedSchedules,
    toggleSaveSchedule
  };
} 